export type PricingRuleType =
  | "CONDITIONAL_DISCOUNT"
  | "TIERED_PRICING"
  | "VOLUME_DISCOUNT"
  | "MULTI_SESSION"
  | "GROUP_SIZE"
  | "EARLY_BIRD"
  | "LAST_MINUTE"
  | "PEAK_TIME"
  | "MEMBER_PRICING"
  | "DEPOSIT"
  | "SLIDING_SCALE"
  | "BOGO"
  | "CAPACITY_BASED_PRICING"
  | "SESSION_BUNDLE"
  | "RENTAL_FRACTION_TIERS";

export type PricingRuleScope = "ticket" | "cart";

export type DiscountType = "percentage" | "fixed_per_unit" | "fixed_total" | "price_override";

export type ChargeCadence =
  | "per_ticket_per_session"
  | "flat"
  | "per_session"
  | "per_course";

export const CHARGE_CADENCES: ChargeCadence[] = [
  "per_ticket_per_session",
  "flat",
  "per_session",
  "per_course",
];

export function scalesWithQuantity(c: ChargeCadence): boolean {
  return c === "per_ticket_per_session" || c === "per_course";
}

export function scalesWithSessions(c: ChargeCadence): boolean {
  return c === "per_ticket_per_session" || c === "per_session";
}

export type ConditionType =
  | "MIN_QUANTITY"
  | "MIN_SESSIONS"
  | "SESSION_WINDOW"
  | "BOOKING_ADVANCE"
  | "LAST_MINUTE"
  | "DAY_OF_WEEK"
  | "TIME_OF_DAY"
  | "MEMBER_TAGS"
  | "CAPACITY_THRESHOLD"
  | "TICKET_COMBINATION"
  | "MIN_SPEND"
  | "RETURNING_CUSTOMER"
  | "FIRST_TIME_BOOKING"
  | "BOOKING_COUNT_IN_PERIOD"
  | "REFERRAL_SOURCE"
  | "DATE_RANGE"
  | "SPECIFIC_DATES"
  | "CONSECUTIVE_SESSIONS"
  | "MONTH_RANGE";

// --- Condition Configs ---

export type MinQuantityConditionConfig = { minQuantity: number };
export type MinSessionsConditionConfig = { minSessions: number };
export type SessionWindowConditionConfig = { timeWindowDays: number };
export type BookingAdvanceConditionConfig = { daysBeforeEvent: number };
export type LastMinuteConditionConfig = { hoursBeforeEvent: number };
export type DayOfWeekConditionConfig = { days: number[] };
export type TimeOfDayConditionConfig = { startTime: string; endTime: string };
export type MemberTagsConditionConfig = { requiredTags: string[]; matchType: "any" | "all" };
export type CapacityThresholdConditionConfig = { maxSoldPercentage: number };
export type TicketCombinationConditionConfig = { requiredTicketIds: string[] };
export type MinSpendConditionConfig = { minAmount: number };
export type ReturningCustomerConditionConfig = { minPreviousBookings: number };
export type FirstTimeBookingConditionConfig = Record<string, never>;
export type BookingCountInPeriodConditionConfig = { minBookings: number; periodDays: number };
export type ReferralSourceConditionConfig = { sources: string[] };
export type DateRangeConditionConfig = { startDate: string; endDate: string };
export type SpecificDatesConditionConfig = { dates: string[] };
export type ConsecutiveSessionsConditionConfig = { minConsecutive: number };
export type RecurringDateRangeConditionConfig = { startDay: number; startMonth: number; endDay: number; endMonth: number };

export type ConditionConfig =
  | MinQuantityConditionConfig
  | MinSessionsConditionConfig
  | SessionWindowConditionConfig
  | BookingAdvanceConditionConfig
  | LastMinuteConditionConfig
  | DayOfWeekConditionConfig
  | TimeOfDayConditionConfig
  | MemberTagsConditionConfig
  | CapacityThresholdConditionConfig
  | TicketCombinationConditionConfig
  | MinSpendConditionConfig
  | ReturningCustomerConditionConfig
  | FirstTimeBookingConditionConfig
  | BookingCountInPeriodConditionConfig
  | ReferralSourceConditionConfig
  | DateRangeConditionConfig
  | SpecificDatesConditionConfig
  | ConsecutiveSessionsConditionConfig
  | RecurringDateRangeConditionConfig;

export type PricingCondition = {
  id: string;
  pricingRuleId: string;
  conditionType: ConditionType;
  config: ConditionConfig;
};

export type PricingPreset =
  | "early_bird"
  | "volume_discount"
  | "multi_session"
  | "session_bundle"
  | "member_pricing"
  | "peak_off_peak"
  | "last_minute"
  | "capacity_threshold"
  | "custom";

export type TicketChargeType = "standard" | "deposit" | "pay_what_you_want" | "free";

export type DepositChargeConfig = {
  depositType: "percentage" | "fixed";
  depositValue: number;
  remainderDue: "on_arrival" | "days_before";
  daysBefore?: number;
  /** How the balance is collected. Absent on legacy configs → treat as "in_person". */
  remainderCollection?: "payment_link" | "in_person";
  sendReminder: boolean;
  reminderDaysBefore?: number;
};

/**
 * One parcel of an N-instalment payment plan (mirrors `@obby/lib`). Row 1 is
 * the deposit, charged at checkout; rows 2..N are collected later.
 */
export type InstalmentRowConfig = {
  label?: string;
  /** "remainder" = whatever is left after every other row — last row only. */
  amountType: "percentage" | "fixed" | "remainder";
  /** Percent or pence per `amountType`; ignored for "remainder" (write 0). */
  amountValue: number;
  dueType: "at_checkout" | "days_before_event" | "days_after_purchase" | "on_arrival";
  dueDays?: number;
  collection: "payment_link" | "in_person";
  offlineNote?: string;
};

export type InstalmentPlanConfig = {
  /** Length 2..12; rows[0] is the deposit (at_checkout). */
  rows: InstalmentRowConfig[];
  sendReminder: boolean;
  reminderDaysBefore?: number;
};

/**
 * The N-instalment shape of a deposit ticket's `chargeConfig` (mirrors
 * `@obby/lib`). A deposit ticket carries EITHER the legacy
 * `DepositChargeConfig` OR this — read through `resolveInstalmentPlan`.
 */
export type InstalmentPlanChargeConfig = {
  instalmentPlan: InstalmentPlanConfig;
};

export type PwywChargeConfig = {
  minPrice: number;
  maxPrice?: number;
  suggestedPrice: number;
  suggestedOptions?: number[];
  allowZero: boolean;
  message?: string;
};

/** Charge a single flat total per order regardless of quantity.
 * Only valid alongside chargeType: "standard". Quantity continues to drive
 * session capacity (when affectsAvailability is true) but does NOT multiply
 * the price. Per-ticket pricing rules are skipped for flat-per-order tickets. */
export type FlatPerOrderChargeConfig = {
  flatPerOrder: true;
};

/** Charge a flat amount per session regardless of quantity. Only valid when
 * chargeType is "standard" AND the course is termly (multi-session). The
 * pricing engine emits totalPerOrder = basePrice (the per-session flat); the
 * consumer multiplies by numberOfSessions to get the line total — same shape
 * as standard tickets, but quantity doesn't multiply. Per-ticket pricing
 * rules are skipped. Mutually exclusive with flatPerOrder. */
export type FlatPerSessionChargeConfig = {
  flatPerSession: true;
};

/** Charge a fixed amount per person for the entire course, regardless of
 * the number of sessions. Only valid when chargeType is "standard" AND the
 * course is termly. The pricing engine multiplies basePrice by quantity
 * (per-person), but the consumer does NOT multiply by numberOfSessions —
 * each ticket buys a "whole-course pass" at basePrice. Mutually exclusive
 * with flatPerOrder and flatPerSession. */
export type FlatPerCourseChargeConfig = {
  flatPerCourse: true;
};

export type PricingRuleStatus = "enabled" | "disabled" | "archived";

export type ModificationActionType =
  | "percentage_decrease"
  | "fixed_decrease"
  | "percentage_increase"
  | "fixed_increase"
  | "absolute_override";

export type ModificationAction = {
  type: ModificationActionType;
  value: number;
};

export type PricingConditionGroup = {
  id: string;
  conditions: PricingCondition[];
};

export type TicketConstraintType =
  | "REQUIRES_TICKET"
  | "REQUIRES_ANY_OF"
  | "MAX_PER_OTHER"
  | "MUTUALLY_EXCLUSIVE"
  | "MIN_TOTAL_TICKETS"
  | "MAX_TOTAL_TICKETS"
  | "EXACT_MULTIPLES"
  | "AVAILABLE_IF_SOLD_OUT"
  | "SESSION_BUNDLE"
  | "REQUIRES_PRIOR_PURCHASE";

// --- Ticket Types ---

export type TicketConstraint = {
  type: TicketConstraintType;
  targetTicketId?: string;
  targetTicketIds?: string[];
  minQuantity: number;
  maxQuantity?: number;
  /** For EXACT_MULTIPLES: tickets must be purchased in multiples of this value */
  multipleOf?: number;
  /** For SESSION_BUNDLE: minimum number of selected sessions required */
  minSessions?: number;
  /** For SESSION_BUNDLE: maximum number of selected sessions covered (null/undefined = no cap) */
  maxSessions?: number | null;
  /** For SESSION_BUNDLE: all sessions must fall within this many days */
  timeWindowDays?: number;
  errorMessage?: string;
  /** For REQUIRES_PRIOR_PURCHASE: how to render the ticket while unmet — "locked" (default) or "hidden" */
  unmetDisplay?: "locked" | "hidden" | null;
};

export type TicketConstraintGroup = {
  id: string;
  constraints: TicketConstraint[];
};

export type TicketTypeV2 = {
  id: string;
  courseId: string;
  schoolId: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  chargeType: TicketChargeType;
  chargeConfig?:
    | DepositChargeConfig
    | InstalmentPlanChargeConfig
    | PwywChargeConfig
    | FlatPerOrderChargeConfig
    | FlatPerSessionChargeConfig
    | FlatPerCourseChargeConfig
    | null;
  templateId?: string;
  affectsAvailability: boolean;
  minTicketsPerOrder: number;
  maxTicketsPerOrder?: number;
  constraints: TicketConstraint[];
  constraintGroups?: TicketConstraintGroup[];
  availableFrom?: Date | string;
  availableUntil?: Date | string;
  availableUntilHoursBefore?: number;
  isVatIncluded: boolean;
  vat?: number;
  status: "enabled" | "disabled" | "archived";
  visibility: "public" | "hidden";
  sortOrder: number;
};

// --- Pricing Rule Configs ---

export type TieredPricingConfig = {
  basePrice: number;
  minTickets: number;
  incrementPrice: number;
  maxTickets?: number;
};

export type VolumeDiscountConfig = {
  thresholds: Array<{
    minQuantity: number;
    discountType: "percentage" | "flat" | "pricePerUnit";
    discountValue: number;
  }>;
};

export type MultiSessionConfig = {
  minSessions: number;
  discountType: "percentage" | "flat" | "pricePerSession";
  discountValue: number;
  applicableTo: "all" | "afterFirst";
};

export type GroupSizeConfig = {
  tiers: Array<{
    minSize: number;
    maxSize: number;
    pricePerPerson: number;
  }>;
};

export type EarlyBirdConfig = {
  deadlineType: "daysBeforeEvent" | "specificDate";
  deadlineValue: number | string;
  discountType: "percentage" | "flat" | "fixedPrice";
  discountValue: number;
};

export type LastMinuteConfig = {
  hoursBeforeEvent: number;
  discountType: "percentage" | "flat";
  discountValue: number;
  minPrice?: number;
};

export type PeakTimeRule = {
  name: string;
  daysOfWeek: number[];
  timeStart?: string;
  timeEnd?: string;
  modifierType: "surcharge" | "discount";
  valueType: "flat" | "percentage";
  value: number;
};

export type PeakTimeConfig = {
  rules: PeakTimeRule[];
};

export type MemberPricingConfig = {
  requiredTags: string[];
  matchType: "any" | "all";
  discountType: "percentage" | "flat" | "fixedPrice";
  discountValue: number;
  applyToGuests: boolean;
};

export type DepositConfig = {
  depositType: "percentage" | "fixed";
  depositValue: number;
  remainderDue: "on_arrival" | "days_before";
  daysBefore?: number;
  sendReminder: boolean;
  reminderDaysBefore?: number;
};

export type SlidingScaleConfig = {
  minPrice: number;
  maxPrice?: number;
  suggestedPrice: number;
  suggestedOptions?: number[];
  allowZero: boolean;
  showMessage?: string;
};

export type BogoConfig = {
  /** The ticket type whose presence triggers the discount */
  triggerTicketId: string;
  /** How many of the trigger ticket must be in cart */
  triggerQuantity: number;
  /** How many of the current ticket get discounted */
  discountedQuantity: number;
  discountType: "percentage" | "flat" | "fixedPrice";
  discountValue: number;
  /** Whether the BOGO repeats (e.g. buy 2 adults, every 1 child is 50% off) */
  repeatable: boolean;
};

export type CapacityBasedPricingConfig = {
  tiers: Array<{
    /** Cumulative spot threshold. `null` marks the open-ended final tier —
     *  any spot that does not match an earlier (bounded) tier uses this
     *  price. Only the last tier in the array may be open-ended; the dialog
     *  validates this on save. */
    upToSold: number | null;
    price: number;
  }>;
};

export type SessionBundleConfig = {
  minSessions: number;
  timeWindowDays: number;
};

export type PricingRuleConfig =
  | TieredPricingConfig
  | VolumeDiscountConfig
  | MultiSessionConfig
  | GroupSizeConfig
  | EarlyBirdConfig
  | LastMinuteConfig
  | PeakTimeConfig
  | MemberPricingConfig
  | DepositConfig
  | SlidingScaleConfig
  | BogoConfig
  | CapacityBasedPricingConfig
  | SessionBundleConfig;

// --- Pricing Rule ---

/** One rung of a rental price ladder. Mirrors the `pricingRuleTiers` table. */
export type RentalFractionTier = {
  /** Inclusive lower bound: this rung applies from this many fractions up. */
  minFractions: number;
  /** `per_fraction` multiplies by the fraction count; `flat` is the whole hire. */
  priceMode: "per_fraction" | "flat";
  /** Pence. A per-fraction rate, or a flat all-in hire price. */
  price: number;
};

export type PricingRule = {
  id: string;
  scope: PricingRuleScope;
  ticketTypeV2Id?: string | null;
  courseId?: string | null;
  schoolId?: string | null;
  ruleType: PricingRuleType;
  name: string;
  description?: string;
  priority: number;
  stackable: boolean;
  stackWithOtherScope: boolean;
  discountType?: DiscountType | null;
  discountValue?: number | null;
  config?: PricingRuleConfig | null;
  applicableCourseIds?: string[];
  preset?: PricingPreset | null;
  conditions?: PricingCondition[];
  /** Rungs of a RENTAL_FRACTION_TIERS ladder. Child rows, not `config` Json. */
  tiers?: RentalFractionTier[];
  conditionGroups?: PricingConditionGroup[];
  modificationAction?: ModificationAction | null;
  status: PricingRuleStatus;
  validFrom?: Date | string;
  validUntil?: Date | string;
};

// --- Pricing Context ---

export type PricingContext = {
  ticketType: TicketTypeV2;
  quantity: number;
  totalTicketsInCart: number;
  sessionsCount: number;
  sessionStartTime: Date;
  bookingTime: Date;
  sessionDayOfWeek: number;
  sessionTimeOfDay: string;
  /**
   * Calendar month (1-12) and day-of-month the hire starts on, in the SCHOOL's
   * timezone. Same purpose as sessionDayOfWeek/sessionTimeOfDay: a date read
   * off the raw instant with `getMonth()`/`getDate()` is whatever calendar day
   * the RUNTIME is in, which is the customer's browser on one side and a UTC
   * container on the other — for a hire near midnight those are different
   * dates, so a MONTH_RANGE rule applied on one side and declined on the
   * other. Set only by `priceRentalHire`; every other caller leaves these
   * undefined and keeps the runtime-local reading.
   */
  sessionMonth?: number;
  sessionDayOfMonth?: number;
  /**
   * The hire's calendar date in the SCHOOL's timezone, `YYYY-MM-DD`. Same
   * reason as the two above, one step further: DATE_RANGE and SPECIFIC_DATES
   * derive their date with `toISOString().slice(0, 10)`, which is UTC. Both
   * sides agree on it — so there is no client/server drift — but for a school
   * ahead of UTC a hire starting at 00:30 local resolves to the PREVIOUS day,
   * and a rule configured for the hire's actual date declines while one
   * configured for the day before applies.
   */
  sessionDateISO?: string;
  customerTags: string[];
  /**
   * Customer booking history at the SCHOOL for the buyer's email, resolved
   * server-side (apps/api CustomerBookingHistory.service) and echoed to the
   * widget by the course endpoint. `undefined` = buyer unknown => every
   * history condition fails closed (no discount). Counts are clamped to the
   * largest threshold any rule on the listing asks for — the evaluators only
   * compare `=== 0` / `>= min`, and the clamp bounds what the public course
   * endpoint reveals about a client-asserted email.
   */
  customerPreviousBookings?: number;
  /** periodDays => paid bookings placed within the last periodDays. One bucket
   *  per distinct `periodDays` configured on the listing's
   *  BOOKING_COUNT_IN_PERIOD rules (a single number could not serve two rules
   *  with different periods). */
  customerBookingsByPeriod?: Record<number, number>;
  schoolTimezone: string;
  customerChosenPrice?: number;
  /** Rental hires only: how many fractions the customer picked. Set by
   *  `priceRentalHire` and by nothing else, which is what keeps
   *  RENTAL_FRACTION_TIERS off every other booking type. */
  fractions?: number;
  /** Full cart contents — needed for cross-ticket rules like BOGO */
  cart?: CartItem[];
  /** Number of spots already sold in this session — needed for CAPACITY_BASED_PRICING */
  currentSoldCount?: number;
  selectedSessions?: Array<{ date: Date | string }>;
};

// --- Pricing Result ---

export type TieredBreakdownLine = {
  unitPrice: number;
  quantity: number;
  lineTotal: number;
};

export type AppliedRule = {
  ruleId: string;
  ruleName: string;
  ruleType: PricingRuleType;
  scope?: PricingRuleScope;
  adjustment: number;
  description: string;
  tieredBreakdown?: TieredBreakdownLine[];
};

export type PricingResult = {
  basePrice: number;
  finalPrice: number;
  totalPerOrder: number;
  /** How many of the `quantity` tickets the price actually charges. Below
   *  `quantity` only when a TIERED_PRICING `maxTickets` cap leaves the tickets
   *  past it free; per-ticket amounts (a fixed deposit) count these, never the
   *  free ones. Set by `evaluatePricingRules`; absent elsewhere = `quantity`. */
  chargedQuantity?: number;
  depositAmount: number | null;
  depositRemainder: number | null;
  depositRemainderDue: string | null;
  appliedRules: AppliedRule[];
  breakdown: PriceBreakdownLine[];
  isSlidingScale: boolean;
  slidingScaleConfig: SlidingScaleConfig | null;
  eligibility?: {
    eligible: boolean;
    message?: string;
    failedRules?: Array<{ ruleType: PricingRuleType; message: string }>;
  };
};

export type PriceBreakdownLine = {
  label: string;
  amount: number;
  type: "base" | "tier" | "override" | "discount" | "surcharge" | "deposit" | "total" | "cart_discount";
};

// --- Cart & Validation ---

export type CartItem = {
  ticketTypeId: string;
  quantity: number;
};

export type ValidationError = {
  ticketTypeId: string;
  constraintType: TicketConstraintType;
  message: string;
};

/**
 * Optional context used when evaluating condition-gated constraints.
 * All fields optional — supply what you have at the call site.
 */
export type ValidationConditionContext = {
  bookingTime?: Date;
  customerTags?: string[];
  sessionStartTime?: Date;
  sessionDayOfWeek?: number;
  sessionTimeOfDay?: string;
  schoolTimezone?: string;
  totalCartAmount?: number;
  currentSoldCount?: number;
  sessionCapacity?: number;
  customerPreviousBookings?: number;
  customerBookingsByPeriod?: Record<number, number>;
  customerReferralSource?: string;
  /** For REQUIRES_PRIOR_PURCHASE: rule-target ticket ids the customer previously purchased (filtered subset, never full history) */
  priorPurchasedTicketIds?: string[];
};

export type ValidationResult = {
  isValid: boolean;
  errors: ValidationError[];
};

// --- Cart-Level Pricing ---

export type CartPricingContext = {
  cart: CartItem[];
  ticketTypes: TicketTypeV2[];
  totalCartAmount: number;
  sessionsCount: number;
  selectedSessions?: Array<{ date: Date | string }>;
  bookingTime: Date;
  customerTags: string[];
  sessionStartTime: Date;
  sessionDayOfWeek: number;
  sessionTimeOfDay: string;
  /**
   * Calendar month (1-12) and day-of-month the hire starts on, in the SCHOOL's
   * timezone. Same purpose as sessionDayOfWeek/sessionTimeOfDay: a date read
   * off the raw instant with `getMonth()`/`getDate()` is whatever calendar day
   * the RUNTIME is in, which is the customer's browser on one side and a UTC
   * container on the other — for a hire near midnight those are different
   * dates, so a MONTH_RANGE rule applied on one side and declined on the
   * other. Set only by `priceRentalHire`; every other caller leaves these
   * undefined and keeps the runtime-local reading.
   */
  sessionMonth?: number;
  sessionDayOfMonth?: number;
  /**
   * The hire's calendar date in the SCHOOL's timezone, `YYYY-MM-DD`. Same
   * reason as the two above, one step further: DATE_RANGE and SPECIFIC_DATES
   * derive their date with `toISOString().slice(0, 10)`, which is UTC. Both
   * sides agree on it — so there is no client/server drift — but for a school
   * ahead of UTC a hire starting at 00:30 local resolves to the PREVIOUS day,
   * and a rule configured for the hire's actual date declines while one
   * configured for the day before applies.
   */
  sessionDateISO?: string;
  schoolTimezone: string;
  currentSoldCount?: number;
  sessionCapacity?: number;
  customerPreviousBookings?: number;
  customerBookingsByPeriod?: Record<number, number>;
  customerReferralSource?: string;
};

export type CartPricingResult = {
  subtotal: number;
  cartDiscounts: AppliedRule[];
  cartAdjustment: number;
  grandTotal: number;
  breakdown: PriceBreakdownLine[];
};
